/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */

import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import GoogleAuthButton from '../../../shared/components/logIn/singleSignOn/GoogleAuthButton';
import MicrosoftAuthButton from '../../../shared/components/logIn/singleSignOn/MicrosoftAuthButton';
import OpenIDAuthButton from '../../../shared/components/logIn/singleSignOn/OpenIDAuthButton';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { useSignupScreen1 } from './useSignupScreen1';

const PASSWORD_VALIDATOR = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.{8,}).*/;
const EMAIL_VALIDATOR =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface SignupScreen1Props {
  clientId: string | undefined;
  domainValidation: boolean;
  error: string | undefined;
  fandfUser: any;
  isGoogleAuthActive: boolean;
  isMicrosoftAuthActive: boolean;
  isStandardAuthActive: boolean;
  singleSignOn: any[];
  handleContinue: (dataObj: any) => void;
  handleResponseSSO: (response: any) => void;
}

export function SignupScreen1({
  clientId,
  domainValidation,
  error,
  fandfUser,
  isGoogleAuthActive,
  isMicrosoftAuthActive,
  isStandardAuthActive,
  singleSignOn,
  handleContinue,
  handleResponseSSO,
}: SignupScreen1Props) {
  const { t } = useTranslation();

  const {
    confirmPasswordInputRef,
    emailInputRef,
    errorMessage,
    handleSubmit,
    isFormLoading,
    register,
    formState: { errors },
    watch,
    showPassword,
    showConfirmPassword,
    passwordInputRef,
    handleContinue: handleContinueForm,
    handleShowPasswordClick,
    handleShowConfirmPasswordClick,
    handleFieldChange,
    setErrorMessage,
    setCompLoading,
  } = useSignupScreen1({
    clientId,
    domainValidation,
    error,
    fandfUser,
    isGoogleAuthActive,
    isMicrosoftAuthActive,
    isStandardAuthActive,
    singleSignOn,
    handleContinue,
    handleResponseSSO,
  });

  return (
    <>
      <h1>{t('label.signup')}</h1>

      {errorMessage && (
        <Alert
          type="error"
          message={errorMessage}
          id="signup-screen1-error"
          isVisible
          isAssessive
        />
      )}

      {isStandardAuthActive && (
        <form onSubmit={handleSubmit(handleContinueForm)}>
          {/* Email Field */}
          <label className="not_logged_label" htmlFor="email">
            {errorMessage
              ? `${t('error.prefix')} ${domainValidation ? t('R045') : t('R023')}`
              : domainValidation
                ? t('R045')
                : t('R023')}
          </label>
          <input
            ref={emailInputRef}
            type="email"
            className="code_input"
            id="email"
            {...register('email', {
              required: true,
              pattern: EMAIL_VALIDATOR,
            })}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            onChange={() => handleFieldChange('email')}
          />
          {errors.email && (
            <Alert
              id="email-error"
              type="error"
              message={t('signin.email.error')}
              isVisible
              size="sm"
            />
          )}

          {/* Password Fieldset */}
          <fieldset className="password_container" aria-labelledby="password-legend">
            <legend id="password-legend" className="not_logged_label">
              {errorMessage ? `${t('error.prefix')} ${t('R024')}` : t('R024')}
            </legend>
            <span className="rules_text">{t('R019')}</span>
            <div className="password_field_wrapper">
              <input
                ref={passwordInputRef}
                type={showPassword ? 'text' : 'password'}
                className="code_input"
                id="password"
                {...register('password', {
                  required: true,
                  pattern: PASSWORD_VALIDATOR,
                })}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
                onChange={() => handleFieldChange('password')}
              />
              <Button
                ariaPressed={showPassword}
                ariaLabel={showPassword ? t('R094') : t('R093')}
                onClick={handleShowPasswordClick}
                variant="icon-only"
                color="pink"
                icon={{
                  faIconDefinition: showPassword ? faEye : faEyeSlash,
                  color: 'white',
                  size: 'sm',
                  className: 'icon',
                }}
              />
            </div>
            {errors.password && (
              <Alert
                id="password-error"
                type="error"
                message={
                  errors.password.type === 'required' ? t('signup.password.required') : t('R019')
                }
                isVisible
                size="sm"
              />
            )}
          </fieldset>

          {/* Confirm Password Fieldset */}
          <fieldset className="password_container" aria-labelledby="confirm-password-legend">
            <legend id="confirm-password-legend" className="not_logged_label">
              {errorMessage ? `${t('error.prefix')} ${t('R025')}` : t('R025')}
            </legend>
            <div className="password_field_wrapper">
              <input
                ref={confirmPasswordInputRef}
                type={showConfirmPassword ? 'text' : 'password'}
                className="code_input"
                id="confirm_password"
                {...register('confirm_password', {
                  required: true,
                  pattern: PASSWORD_VALIDATOR,
                  validate: (value) => value === watch('password'),
                })}
                aria-invalid={!!errors.confirm_password}
                aria-describedby={errors.confirm_password ? 'confirm-password-error' : undefined}
                onChange={() => handleFieldChange('confirm_password')}
              />
              <Button
                ariaPressed={showConfirmPassword}
                ariaLabel={`${t('label.confirm')} ${showConfirmPassword ? t('R094') : t('R093')}`}
                onClick={handleShowConfirmPasswordClick}
                variant="icon-only"
                icon={{
                  faIconDefinition: showConfirmPassword ? faEye : faEyeSlash,
                  color: 'white',
                  size: 'sm',
                  className: 'icon',
                }}
              />
            </div>
            {errors.confirm_password && (
              <Alert
                id="confirm-password-error"
                type="error"
                message={
                  errors.confirm_password.type === 'required'
                    ? t('signup.confirmPassword.required')
                    : errors.confirm_password.type === 'pattern'
                      ? t('R019')
                      : t('signin.confirmPassword.error')
                }
                isVisible
                size="sm"
              />
            )}
          </fieldset>

          {!isFormLoading && (
            <Button type="submit" id="continue">
              {t('R013')}
            </Button>
          )}
        </form>
      )}

      {isFormLoading && <LoadingSpinner asOverlay />}

      <div className="sso_container">
        {!isFormLoading &&
          isStandardAuthActive &&
          (isMicrosoftAuthActive || isGoogleAuthActive || singleSignOn.length > 0) && (
            <p className="or">{t('R071')}</p>
          )}

        {!isFormLoading && isMicrosoftAuthActive && (
          <MicrosoftAuthButton
            handleResponse={handleResponseSSO}
            fandfUser={fandfUser}
            clientid={clientId}
            setErrorMessage={setErrorMessage}
            setCompLoading={setCompLoading}
          />
        )}
        {!isFormLoading && isGoogleAuthActive && (
          <GoogleAuthButton
            handleResponse={handleResponseSSO}
            fandfUser={fandfUser}
            clientid={clientId}
            setErrorMessage={setErrorMessage}
            setCompLoading={setCompLoading}
          />
        )}
        {!isFormLoading &&
          singleSignOn?.map((sso) => (
            <OpenIDAuthButton
              key={sso._id}
              configurationName={sso.type}
              ssoId={sso.id}
              redirectUri={`${window.location.origin}/sign-in/callback`}
              scope={sso.scope}
              authority={sso.issuer}
              secret={sso.secret}
              clientId={clientId}
              setErrorMessage={setErrorMessage}
              handleResponse={handleResponseSSO}
              setCompLoading={setCompLoading}
            />
          ))}
      </div>
    </>
  );
}
